import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Hub, Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import type { TransferGroup } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import { RequirePermission, usePermissions } from '../contexts/PermissionContext';
import { useListSearchParams } from '../hooks/useListSearchParams';

interface TransferGroupListResponse {
  count?: number;
  results?: TransferGroup[];
}

const STRATEGY_CHIP_COLOR: Record<TransferGroup['strategy'], 'default' | 'primary' | 'secondary' | 'info'> = {
  rrmemory: 'primary',
  leastrecent: 'info',
  fewestcalls: 'secondary',
  ringall: 'default',
  agent_selected: 'info',
};

export default function TransferGroupsPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:transferGroups.title'));
  const { hasPermission } = usePermissions();
  const [rows, setRows] = useState<TransferGroup[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    filters,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    setParams,
    getPageLink,
  } = useListSearchParams({
    filters: ['search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: 'name',
  });

  const searchValue = filters.search ?? '';

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<TransferGroupListResponse>('/api/transfer-groups/', {
        params: {
          page: page + 1,
          page_size: rowsPerPage,
          search: searchValue || undefined,
        },
      });
      const payload = response.data;
      const listRows = Array.isArray(payload.results) ? payload.results : [];
      setRows(listRows);
      setTotalRows(Number(payload.count ?? listRows.length ?? 0));
    } catch (requestError: unknown) {
      console.error('Error loading transfer groups:', requestError);
      setRows([]);
      setTotalRows(0);
      setError(t('pages:transferGroups.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, t]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleDelete = async (id: string) => {
    if (!confirm(t('pages:transferGroups.deleteConfirm'))) {
      return;
    }
    try {
      await api.delete(`/api/transfer-groups/${id}/`);
      loadRows();
    } catch (requestError: unknown) {
      console.error('Error deleting transfer group:', requestError);
      setError(t('pages:transferGroups.errors.delete'));
    }
  };

  const strategyLabel = useMemo(
    () => (value: TransferGroup['strategy']) => t(`pages:transferGroups.strategyOptions.${value}`),
    [t],
  );

  const headerActions = (
    <>
      <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={loadRows}>
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="transfer_groups_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/transfer-groups/new"
        >
          {t('pages:transferGroups.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  if (!hasPermission('transfer_groups_read')) {
    return <Alert severity="warning">{t('pages:transferGroups.accessDenied')}</Alert>;
  }

  const filterControls = (
    <TextField
      size="small"
      label={t('common:actions.search')}
      placeholder={t('pages:transferGroups.filters.searchPlaceholder')}
      value={searchValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setParams({ search: value || undefined }, { resetPage: true });
      }}
      sx={{ minWidth: 280, flex: '1 1 280px' }}
    />
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('common:common.name')}</TableCell>
          <TableCell>{t('pages:transferGroups.columns.strategy')}</TableCell>
          <TableCell>{t('pages:transferGroups.columns.members')}</TableCell>
          <TableCell>{t('pages:transferGroups.columns.status')}</TableCell>
          <TableCell>{t('common:common.updatedAt')}</TableCell>
          <TableCell align="right">{t('common:actions.actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={6}>
              <Typography color="text.secondary">{t('common:common.loading')}</Typography>
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6}>
              <Typography color="text.secondary">{t('pages:transferGroups.empty')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((group) => (
            <TableRow key={group.id} hover>
              <LinkTableCell to={`/transfer-groups/${group.id}`}>{group.name}</LinkTableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={STRATEGY_CHIP_COLOR[group.strategy]}
                  label={strategyLabel(group.strategy)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{group.membersCount}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={group.isActive ? 'success' : 'default'}
                  label={group.isActive ? t('common:status.active') : t('common:status.inactive')}
                />
              </TableCell>
              <TableCell>{new Date(group.updatedAt).toLocaleString()}</TableCell>
              <TableCell align="right">
                <RequirePermission permission="transfer_groups_update">
                  <IconButton component={RouterLink} to={`/transfer-groups/${group.id}`} size="small">
                    <Edit fontSize="small" />
                  </IconButton>
                </RequirePermission>
                <RequirePermission permission="transfer_groups_delete">
                  <IconButton color="error" size="small" onClick={() => handleDelete(group.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <FilteredListLayout
      headerProps={{
        icon: <Hub />,
        title: t('pages:transferGroups.title'),
        subtitle: t('pages:transferGroups.subtitle'),
        actions: headerActions,
      }}
      filterControls={filterControls}
      errorContent={error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      table={table}
      tableFooter={(
        <LinkPagination
          count={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
          getPageLink={getPageLink}
        />
      )}
    />
  );
}
